import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

export interface FilterState {
  tier: string;
  intent: string;
  buyer: string;
  state: string;
  search: string;
}

interface FilterBarProps {
  onFilterChange: (filters: FilterState) => void;
}

interface Buyer {
  id: string;
  name: string;
}

export function FilterBar({ onFilterChange }: FilterBarProps) {
  const [filters, setFilters] = useState<FilterState>({
    tier: 'all',
    intent: 'all',
    buyer: 'all',
    state: 'all',
    search: '',
  });
  const [buyers, setBuyers] = useState<Buyer[]>([]);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) return;
    trpc.network.listBuyers.query({ tenantId: user.tenantId })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((result: any) => {
        const list = result?.buyers ?? [];
        setBuyers(list);
      })
      .catch(console.error);
  }, []);

  function update(key: keyof FilterState, value: string) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    onFilterChange(next);
  }

  function clearAll() {
    const reset: FilterState = { tier: 'all', intent: 'all', buyer: 'all', state: 'all', search: '' };
    setFilters(reset);
    onFilterChange(reset);
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search ANI, state, summary..."
            value={filters.search}
            onChange={e => update('search', e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Tier */}
        <select
          value={filters.tier}
          onChange={e => update('tier', e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Tiers</option>
          <option value="tier1">Tier 1</option>
          <option value="tier2">Tier 2</option>
          <option value="tier3">Tier 3</option>
          <option value="unqualified">Unqualified</option>
        </select>

        {/* Intent */}
        <select
          value={filters.intent}
          onChange={e => update('intent', e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Intents</option>
          <option value="sales">Sales</option>
          <option value="service">Service</option>
          <option value="ivr_only">IVR Only</option>
          <option value="unknown">Unknown</option>
        </select>

        {/* Buyer */}
        <select
          value={filters.buyer}
          onChange={e => update('buyer', e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All Buyers</option>
          {buyers.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>

        {/* State */}
        <select
          value={filters.state}
          onChange={e => update('state', e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="all">All States</option>
          {US_STATES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Clear */}
        <button
          onClick={clearAll}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-slate-800 transition-colors"
        >
          Clear filters
        </button>
      </div>
    </div>
  );
}
